import { CartAddEvent } from '@theme/events';

// Shared across the drawer and cart page copies so a trim only runs once.
let trimming = false;

/**
 * Free sample canister progress bar.
 * - Pick buttons add a free canister (tagged with the hidden _free_canister property).
 * - If the cart drops below the tier, extra free canisters are taken back out
 *   so nobody is charged $4 by surprise. Canisters bought on their own are never touched.
 */
class CanisterProgress extends HTMLElement {
  static observedAttributes = ['data-excess'];

  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#trimExcess();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#trimExcess();
  }

  get #sectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  #showError(message) {
    const el = this.querySelector('.canister-progress__error');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  #announce(cart, sections) {
    document.dispatchEvent(
      new CartAddEvent(cart || {}, 'canister-progress', {
        source: 'canister-progress',
        itemCount: cart?.item_count,
        sections: sections || {},
      })
    );
  }

  #onClick = async (event) => {
    const button = event.target.closest('.canister-progress__pick');
    if (!button || this.#busy) return;
    this.#busy = true;
    this.querySelectorAll('.canister-progress__pick').forEach((b) => (b.disabled = true));

    try {
      const sections = this.#sectionIds;
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1, properties: { _free_canister: '1' } }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add canister');
      this.#announce(null, data.sections);
    } catch (err) {
      console.error(err);
      this.#showError('Sorry, that canister could not be added. Please try again.');
      this.querySelectorAll('.canister-progress__pick').forEach((b) => (b.disabled = false));
    } finally {
      this.#busy = false;
    }
  };

  async #trimExcess() {
    // Wait for the section morph to finish so both data attributes are current.
    await new Promise((r) => setTimeout(r, 50));
    let excess = parseInt(this.dataset.excess || '0', 10);
    if (!excess || trimming) return;
    trimming = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      // Newest free canisters come off first.
      const lines = cart.items
        .filter((item) => item.properties && item.properties._free_canister)
        .reverse();
      const sections = this.#sectionIds;
      let data = null;
      for (const line of lines) {
        if (excess <= 0) break;
        const take = Math.min(excess, line.quantity);
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: line.key,
            quantity: line.quantity - take,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });
        data = await res.json();
        if (!res.ok) throw new Error(data.description || 'Could not update cart');
        excess -= take;
      }
      if (data) this.#announce(data, data.sections);
    } catch (err) {
      console.error(err);
    } finally {
      trimming = false;
    }
  }
}

if (!customElements.get('canister-progress')) {
  customElements.define('canister-progress', CanisterProgress);
}

/**
 * Slim strip under the header that follows the cart total on every page.
 */
// Whole dollars show without cents ($75), anything else keeps them ($1.15).
const money = (cents) => `$${cents % 100 === 0 ? cents / 100 : (cents / 100).toFixed(2)}`;

class CanisterStrip extends HTMLElement {
  connectedCallback() {
    this.render(parseInt(this.dataset.qualifying || '0', 10), parseInt(this.dataset.free || '0', 10));
    document.addEventListener('cart:update', this.#refresh);
    document.addEventListener('discount:update', this.#refresh);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.#refresh);
    document.removeEventListener('discount:update', this.#refresh);
  }

  #refresh = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`).then((r) => r.json());
      let qualifying = 0;
      let free = 0;
      for (const item of cart.items) {
        if (item.handle === 'sample-canister') {
          if (item.properties && item.properties._free_canister) free += item.quantity;
          continue;
        }
        const type = item.product_type || '';
        if (type === 'Sample' || type === 'Holiday Gifts' || type === 'Alternative Milk' || type.includes('Wholesale')) continue;
        qualifying += item.final_line_price;
      }
      this.dataset.total = String(cart.total_price || 0);
      this.render(qualifying, free);
    } catch (err) {
      console.error(err);
    }
  };

  render(qualifying, free) {
    const msg = this.querySelector('.canister-strip__msg');
    const fill = this.querySelector('.canister-strip__fill');
    if (!msg || !fill) return;
    // One free sample canister at $55. Coffee and gear count; samples and wholesale items don't.
    const total = parseInt(this.dataset.total || String(qualifying), 10);
    const pct = Math.min(100, Math.round((qualifying / 5500) * 100));
    fill.style.width = `${pct}%`;

    if (qualifying <= 0 && total <= 0) {
      msg.innerHTML = 'Spend $55, get a free sample canister.';
    } else if (qualifying < 5500) {
      msg.innerHTML = `You're <strong>${money(5500 - qualifying)}</strong> away from a free sample canister`;
    } else {
      msg.innerHTML =
        free >= 1
          ? 'Your free sample canister is in the cart'
          : `You earned a free sample canister. <strong>Pick yours in the cart</strong>`;
    }
  }
}

if (!customElements.get('canister-strip')) {
  customElements.define('canister-strip', CanisterStrip);
}
